/**
 * Medical Entity Extraction Service
 * 
 * Extracts medical entities from queries for better search preprocessing.
 * Identifies conditions, medications, procedures, and other medical concepts.
 */

// Medical entity patterns for extraction
const MEDICAL_ENTITY_PATTERNS = {
  // Conditions/Diseases (common patterns)
  conditions: [
    /\b(?:diabetes|hypertension|asthma|copd|pneumonia|sepsis|infection|fever|cough|pain)\b/gi,
    /\b(?:acute|chronic|subacute)\s+(?:condition|disease|illness|syndrome)\b/gi,
  ],
  
  // Medications (common drug names and classes)
  medications: [
    /\b(?:aspirin|ibuprofen|acetaminophen|metformin|insulin|warfarin|amoxicillin|penicillin)\b/gi,
    /\b(?:ACE inhibitor|beta blocker|statin|antibiotic|anticoagulant)\b/gi,
  ],
  
  // Procedures/Tests
  procedures: [
    /\b(?:surgery|operation|procedure|biopsy|endoscopy|colonoscopy|mammogram)\b/gi,
    /\b(?:CT scan|MRI|X-ray|ultrasound|EKG|ECG|blood test|lab test)\b/gi,
  ],
  
  // Body parts/Systems
  anatomy: [
    /\b(?:heart|lung|kidney|liver|brain|stomach|chest|abdomen|extremity)\b/gi,
    /\b(?:cardiovascular|respiratory|gastrointestinal|neurological|musculoskeletal)\b/gi,
  ],
  
  // ICD-10 codes
  icd10: /\b[A-Z]\d{2}(?:\.\d{1,4})?\b/g,
  
  // CPT codes
  cpt: /\b\d{5}(?:-\d{2})?\b/g,
};

/**
 * Extract medical entities from text
 * @param {string} text - Text to extract entities from
 * @returns {Object} Extracted entities by type
 */
export const extractMedicalEntities = (text) => {
  if (!text || typeof text !== 'string') {
    return {
      conditions: [],
      medications: [],
      procedures: [],
      anatomy: [],
      codes: [],
      allTerms: [],
    };
  }

  const entities = {
    conditions: [],
    medications: [],
    procedures: [],
    anatomy: [],
    codes: [],
    allTerms: new Set(),
  };

  // Extract conditions
  MEDICAL_ENTITY_PATTERNS.conditions.forEach(pattern => {
    const matches = text.match(pattern);
    if (matches) {
      entities.conditions.push(...matches.map(m => m.toLowerCase()));
      matches.forEach(m => entities.allTerms.add(m.toLowerCase()));
    }
  });

  // Extract medications
  MEDICAL_ENTITY_PATTERNS.medications.forEach(pattern => {
    const matches = text.match(pattern);
    if (matches) {
      entities.medications.push(...matches.map(m => m.toLowerCase()));
      matches.forEach(m => entities.allTerms.add(m.toLowerCase()));
    }
  });

  // Extract procedures
  MEDICAL_ENTITY_PATTERNS.procedures.forEach(pattern => {
    const matches = text.match(pattern);
    if (matches) {
      entities.procedures.push(...matches.map(m => m.toLowerCase()));
      matches.forEach(m => entities.allTerms.add(m.toLowerCase()));
    }
  });

  // Extract anatomy
  MEDICAL_ENTITY_PATTERNS.anatomy.forEach(pattern => {
    const matches = text.match(pattern);
    if (matches) {
      entities.anatomy.push(...matches.map(m => m.toLowerCase()));
      matches.forEach(m => entities.allTerms.add(m.toLowerCase()));
    }
  });

  // Extract codes
  const icd10Matches = text.match(MEDICAL_ENTITY_PATTERNS.icd10);
  const cptMatches = text.match(MEDICAL_ENTITY_PATTERNS.cpt);
  if (icd10Matches) {
    entities.codes.push(...icd10Matches);
    icd10Matches.forEach(m => entities.allTerms.add(m));
  }
  if (cptMatches) {
    entities.codes.push(...cptMatches);
    cptMatches.forEach(m => entities.allTerms.add(m));
  }

  // Convert Set to Array and remove duplicates from arrays
  return {
    conditions: [...new Set(entities.conditions)],
    medications: [...new Set(entities.medications)],
    procedures: [...new Set(entities.procedures)],
    anatomy: [...new Set(entities.anatomy)],
    codes: [...new Set(entities.codes)],
    allTerms: Array.from(entities.allTerms),
  };
};

/**
 * Expand query with medical synonyms and related terms
 * @param {string} query - Original query
 * @param {Object} entities - Extracted entities
 * @returns {string} Expanded query
 */
export const expandQueryWithMedicalTerms = (query, entities) => {
  // For now, return original query
  // In future, could integrate with medical terminology APIs or synonym databases
  // (e.g., UMLS, SNOMED CT)
  return query;
};

const medicalEntityExtractionService = {
  extractMedicalEntities,
  expandQueryWithMedicalTerms,
};

export default medicalEntityExtractionService;
